import React, { useEffect, useState } from 'react';
import { geoLocationsApi } from '../lib/api-client';
import useLocale from '../contexts/LocaleContext';
import { Input } from './ui/Input';

interface LocationItem {
  id: string;
  name: string;
  locationType: string;
}

interface SingleLocationSelectorProps {
  selectedLocationId: string | undefined;
  onLocationChange: (locationId: string | undefined, level?: string | null) => void;
  className?: string;
  disabled?: boolean;
  compact?: boolean;
  initialHierarchy?: {
    provinceId?: string | null;
    districtId?: string | null;
    sectorId?: string | null;
    cellId?: string | null;
    villageId?: string | null;
  };
  allowChildrenOnly?: boolean;
  minimumLevel?: 'province' | 'district' | 'sector' | 'cell' | 'village';
  maximumLevel?: 'province' | 'district' | 'sector' | 'cell' | 'village';
}

const SingleLocationSelector: React.FC<SingleLocationSelectorProps> = ({
  selectedLocationId,
  onLocationChange,
  className,
  disabled,
  compact = false,
  initialHierarchy,
  allowChildrenOnly,
  minimumLevel,
  maximumLevel,
}) => {
  const { dictionary } = useLocale();
  const [provinces, setProvinces] = useState<LocationItem[]>([]);
  const [districts, setDistricts] = useState<LocationItem[]>([]);
  const [sectors, setSectors] = useState<LocationItem[]>([]);
  const [cells, setCells] = useState<LocationItem[]>([]);
  const [villages, setVillages] = useState<LocationItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedProvince, setSelectedProvince] = useState<string>('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [selectedSector, setSelectedSector] = useState<string>('');
  const [selectedCell, setSelectedCell] = useState<string>('');
  const [selectedVillage, setSelectedVillage] = useState<string>('');

  const isMinimumLevelMet = (): boolean => {
    if (!minimumLevel) return true;
    const levelHierarchy = ['province', 'district', 'sector', 'cell', 'village'];
    const minLevelIndex = levelHierarchy.indexOf(minimumLevel);
    if (selectedVillage) return true;
    if (selectedCell) return levelHierarchy.indexOf('cell') >= minLevelIndex;
    if (selectedSector) return levelHierarchy.indexOf('sector') >= minLevelIndex;
    if (selectedDistrict) return levelHierarchy.indexOf('district') >= minLevelIndex;
    if (selectedProvince) return levelHierarchy.indexOf('province') >= minLevelIndex;
    return false;
  };

  const shouldStopAtLevel = (currentLevel: string): boolean => {
    if (!maximumLevel) return false;
    return currentLevel === maximumLevel;
  };

  const shouldRenderLevel = (level: string): boolean => {
    if (!maximumLevel) return true;
    const levelHierarchy = ['province', 'district', 'sector', 'cell', 'village'];
    const maxLevelIndex = levelHierarchy.indexOf(maximumLevel);
    const currentLevelIndex = levelHierarchy.indexOf(level);
    return currentLevelIndex <= maxLevelIndex;
  };

  useEffect(() => {
    fetchProvinces();
  }, []);

  // Handle initialHierarchy - load all data upfront
  useEffect(() => {
    if (initialHierarchy && provinces.length > 0) {
      const loadInitialData = async () => {
        try {
          // Set province
          if (initialHierarchy.provinceId) {
            setSelectedProvince(initialHierarchy.provinceId);

            // Fetch and set districts
            await fetchSubdivisions(initialHierarchy.provinceId, 'district');

            // Set district if available
            if (initialHierarchy.districtId) {
              setSelectedDistrict(initialHierarchy.districtId);

              // Fetch and set sectors
              await fetchSubdivisions(initialHierarchy.districtId, 'sector');

              // Set sector if available
              if (initialHierarchy.sectorId) {
                setSelectedSector(initialHierarchy.sectorId);

                // Fetch and set cells
                await fetchSubdivisions(initialHierarchy.sectorId, 'cell');

                // Set cell if available
                if (initialHierarchy.cellId) {
                  setSelectedCell(initialHierarchy.cellId);

                  // Fetch and set villages
                  await fetchSubdivisions(initialHierarchy.cellId, 'village');

                  // Set village if available
                  if (initialHierarchy.villageId) {
                    setSelectedVillage(initialHierarchy.villageId);
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('Error loading initial hierarchy:', error);
        }
      };

      loadInitialData();
    }
  }, [initialHierarchy, provinces]);

  const fetchProvinces = async () => {
    setLoading(true);
    try {
      const response = await geoLocationsApi.getProvinces();
      setProvinces(response);
    } catch (error) {
      console.error('Error fetching provinces:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubdivisions = async (parentId: string, level: string) => {
    try {
      const response = await geoLocationsApi.getAddressInfo(parentId);
      const subLocations = response.subLocations?.data || [];

      switch (level) {
        case 'district':
          setDistricts(subLocations);
          break;
        case 'sector':
          setSectors(subLocations);
          break;
        case 'cell':
          setCells(subLocations);
          break;
        case 'village':
          setVillages(subLocations);
          break;
      }
      return subLocations;
    } catch (error) {
      console.error(`Error fetching ${level}s:`, error);
      return [];
    }
  };

  const handleProvinceChange = (provinceId: string) => {
    setSelectedProvince(provinceId);
    setSelectedDistrict('');
    setSelectedSector('');
    setSelectedCell('');
    setSelectedVillage('');
    setDistricts([]);
    setSectors([]);
    setCells([]);
    setVillages([]);
    onLocationChange(provinceId, 'province');

    if (provinceId && !shouldStopAtLevel('province')) {
      fetchSubdivisions(provinceId, 'district');
    }
  };

  const handleDistrictChange = (districtId: string) => {
    setSelectedDistrict(districtId);
    setSelectedSector('');
    setSelectedCell('');
    setSelectedVillage('');
    setSectors([]);
    setCells([]);
    setVillages([]);
    onLocationChange(districtId, 'district');

    if (districtId && !shouldStopAtLevel('district')) {
      fetchSubdivisions(districtId, 'sector');
    }
  };

  const handleSectorChange = (sectorId: string) => {
    setSelectedSector(sectorId);
    setSelectedCell('');
    setSelectedVillage('');
    setCells([]);
    setVillages([]);
    onLocationChange(sectorId, 'sector');

    if (sectorId && !shouldStopAtLevel('sector')) {
      fetchSubdivisions(sectorId, 'cell');
    }
  };

  const handleCellChange = (cellId: string) => {
    setSelectedCell(cellId);
    setSelectedVillage('');
    setVillages([]);
    onLocationChange(cellId, 'cell');

    if (cellId && !shouldStopAtLevel('cell')) {
      fetchSubdivisions(cellId, 'village');
    }
  };

  const handleVillageChange = (villageId: string) => {
    setSelectedVillage(villageId);
    onLocationChange(villageId, 'village');
  };

  const renderSelect = (
    name: string,
    options: LocationItem[],
    label: string,
    placeholder: string,
    value: string,
    onChange: (id: string) => void,
    isDisabled: boolean = false
  ): JSX.Element => {
    const selectOptions = options.map(option => ({ value: option.id, label: option.name }));
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => onChange(String(e.target.value ?? ''));

    return compact ? (
      <Input
        type='select'
        name={name}
        options={selectOptions}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={loading || disabled || isDisabled}
        className='w-[170px] flex-shrink-0'
      />
    ) : (
      <Input
        type='select'
        name={name}
        label={label}
        required
        options={selectOptions}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={loading || disabled || isDisabled}
      />
    );
  };

  if (loading && provinces.length === 0) {
    return (
      <div className={`border-2 border-slate-100 rounded-lg p-3 ${className}`}>
        <div className='text-sm text-slate-500'>{dictionary['location.loading']}</div>
      </div>
    );
  }

  const isProvinceDisabled = allowChildrenOnly && !!initialHierarchy?.provinceId;
  const isDistrictDisabled = allowChildrenOnly && !!initialHierarchy?.districtId;
  const isSectorDisabled = allowChildrenOnly && !!initialHierarchy?.sectorId;
  const isCellDisabled = allowChildrenOnly && !!initialHierarchy?.cellId;

  if (compact) {
    return (
      <div className={`flex flex-wrap items-center gap-2 ${className}`}>
        {shouldRenderLevel('province') && renderSelect('province', provinces, dictionary['form.province'], dictionary['location.select_province'], selectedProvince, handleProvinceChange, allowChildrenOnly && !!initialHierarchy?.provinceId)}
        {shouldRenderLevel('district') && renderSelect('district', districts, dictionary['form.district'], dictionary['location.select_district'], selectedDistrict, handleDistrictChange, !selectedProvince || (allowChildrenOnly && !!initialHierarchy?.districtId))}
        {shouldRenderLevel('sector') && renderSelect('sector', sectors, dictionary['form.sector'], dictionary['location.select_sector'], selectedSector, handleSectorChange, !selectedDistrict || (allowChildrenOnly && !!initialHierarchy?.sectorId))}
        {shouldRenderLevel('cell') && renderSelect('cell', cells, dictionary['form.cell'], dictionary['location.select_cell'], selectedCell, handleCellChange, !selectedSector || (allowChildrenOnly && !!initialHierarchy?.cellId))}
        {shouldRenderLevel('village') && renderSelect('village', villages, dictionary['form.village'], dictionary['location.select_village'], selectedVillage, handleVillageChange, !selectedCell)}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {!allowChildrenOnly && <div className='text-sm text-gray-600 mb-2'>{dictionary['location.select_location']}</div>}

      <div className='grid grid-cols-1 gap-4'>
        {shouldRenderLevel('province') &&
          renderSelect(
            'province',
            provinces,
            dictionary['form.province'],
            dictionary['location.select_province'],
            selectedProvince,
            handleProvinceChange,
            isProvinceDisabled
          )}

        {shouldRenderLevel('district') &&
          selectedProvince &&
          renderSelect(
            'district',
            districts,
            dictionary['form.district'],
            dictionary['location.select_district'],
            selectedDistrict,
            handleDistrictChange,
            isDistrictDisabled
          )}

        {shouldRenderLevel('sector') &&
          selectedDistrict &&
          renderSelect(
            'sector',
            sectors,
            dictionary['form.sector'],
            dictionary['location.select_sector'],
            selectedSector,
            handleSectorChange,
            isSectorDisabled
          )}

        {shouldRenderLevel('cell') &&
          selectedSector &&
          renderSelect(
            'cell',
            cells,
            dictionary['form.cell'],
            dictionary['location.select_cell'],
            selectedCell,
            handleCellChange,
            isCellDisabled
          )}

        {shouldRenderLevel('village') &&
          selectedCell &&
          renderSelect(
            'village',
            villages,
            dictionary['form.village'],
            dictionary['location.select_village'],
            selectedVillage,
            handleVillageChange,
            false
          )}
      </div>

      {minimumLevel && !isMinimumLevelMet() && selectedLocationId && (
        <div className='mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md'>
          <p className='text-sm text-amber-800'>
            ⚠️ {dictionary['location.minimum_level_warning']} <strong className='capitalize'>{minimumLevel}</strong>{' '}
            {dictionary['location.level_to_continue']}
          </p>
        </div>
      )}
    </div>
  );
};

export default SingleLocationSelector;
